export interface BeachActivity {
    icon: string;
    title: string;
    description: string;
}

export interface BeachHighlight {
    title: string;
    description: string;
}

export interface BeachAccessOption {
    icon: string;
    title: string;
    description: string;
}

export interface BeachPracticalInfo {
    label: string;
    value: string;
}

export interface BeachPracticalInformation {
    location: string;
    bestPeriod: string;
    usefulInfo: BeachPracticalInfo[];
    activitiesSummary: string;
    emergencyNumbers?: string;
}

import { beachGalleryImages } from "./beach-gallery-images";

const emergencyNumbers =
    "Police secours (110 / 111 / 170), Gendarmerie (111), Sapeurs-pompiers (180), SAMU (185), Sécurité routière (centre d'appel 1302)";

export interface BeachDestinationDetail {
    slug: string;
    name: string;
    heroImage: string;
    subtitle: string;
    heroTags: { icon: string; label: string }[];
    welcomeTitle: string;
    welcomeParagraphs: string[];
    closingParagraph?: string;
    galleryImages: string[];
    activitiesTitle?: string;
    activitiesSubtitle?: string;
    activities?: BeachActivity[];
    highlightBeaches?: BeachHighlight[];
    safetyGuidelines?: string[];
    accessOptions?: BeachAccessOption[];
    practicalInformation?: BeachPracticalInformation;
}

export const beachDestinationDetails: BeachDestinationDetail[] = [
    {
        slug: "tolou",
        name: "Tolou",
        heroImage: beachGalleryImages.tolou[0],
        subtitle: "Une parenthèse de rêve au bout du monde, entre Tabou et Grand-Béréby",
        heroTags: [
            { icon: "🌿", label: "Village Kroumen" },
            { icon: "🏖️", label: "Plage préservée" },
            { icon: "🌅", label: "Coucher de soleil" },
        ],
        welcomeTitle: "Tolou Plage – Une parenthèse de rêve au bout du monde",
        welcomeParagraphs: [
            "Sur la route reliant Tabou à Grand-Béréby, découvrez Tolou, un authentique village Kroumen où la nature règne en maître et où l'océan dévoile l'un de ses plus beaux secrets.",
            "À seulement quelques centaines de mètres de la route principale, l'aventure commence par une traversée enchanteresse à travers une forêt verdoyante et de magnifiques cocoteraies. Cette balade au cœur d'un décor tropical vous conduit progressivement vers un véritable paradis caché.",
            "Au bout du chemin, Tolou Plage vous accueille avec son sable doré, ses eaux cristallines et son atmosphère paisible. Ici, les vagues douces caressent le rivage et donnent à la mer l'apparence d'une immense piscine naturelle, idéale pour la baignade, la détente et la contemplation.",
            "Loin de l'agitation urbaine, Tolou est une invitation à l'évasion, au ressourcement et à la découverte d'une Côte d'Ivoire authentique. Entre paysages préservés, brise marine et coucher de soleil spectaculaire sur l'Atlantique, chaque instant passé sur cette plage devient un souvenir inoubliable.",
        ],
        closingParagraph:
            "Tolou Plage, un joyau naturel de la commune de Tabou, où le temps semble s'arrêter pour laisser place à la sérénité et à l'émerveillement.",
        galleryImages: beachGalleryImages.tolou,
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Découvrez Tolou au rythme de la nature",
        activities: [
            {
                icon: "🚶",
                title: "Traversée forestière",
                description:
                    "Parcourez la forêt verdoyante et les cocoteraies qui mènent à la plage, une balade immersive au cœur d'un décor tropical.",
            },
            {
                icon: "🏊",
                title: "Baignade & détente",
                description:
                    "Profitez des eaux cristallines et des vagues douces, idéales pour la baignade et la contemplation dans un cadre paisible.",
            },
            {
                icon: "🌅",
                title: "Coucher de soleil",
                description:
                    "Admirez un coucher de soleil spectaculaire sur l'Atlantique, l'un des plus beaux instants à Tolou Plage.",
            },
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre forêt tropicale et plage préservée.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "Problème de réseau internet et d'eau courante.",
            "Idéal pour une escapade.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Tolou, District du Bas-Sassandra, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 428 km" },
                { label: "Temps de trajet", value: "5 h à 5 h 30 par la route, selon les conditions de circulation" },
                { label: "Aéroport le plus proche", value: "Aéroport de San-Pédro" },
                {
                    label: "Centre de santé le plus proche",
                    value: "Hôpital Général de Sassandra (CHR de San-Pédro pour les soins spécialisés)",
                },
                { label: "Température moyenne", value: "25 à 28 °C toute l'année" },
                {
                    label: "Hébergement",
                    value: "Maisons d'hôtes et hôtels à proximité de Sassandra et San-Pédro.",
                },
            ],
            activitiesSummary: "Plongée sous-marine, Baignade, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "monogaga",
        name: "Monogaga",
        heroImage: beachGalleryImages.monogaga[0],
        subtitle: "L'évasion au cœur d'un paradis sauvage entre San Pedro et Sassandra",
        heroTags: [
            { icon: "🌿", label: "Nature préservée" },
            { icon: "🏄", label: "Surf" },
            { icon: "🌅", label: "Couchers de soleil" },
        ],
        welcomeTitle: "Plage de Monogaga – L'évasion au cœur d'un paradis sauvage",
        welcomeParagraphs: [
            "Nichée sur la côte ouest ivoirienne, entre San Pedro et Sassandra, la plage de Monogaga est une destination d'exception où la nature s'exprime dans toute sa splendeur. Véritable trésor caché du littoral atlantique, elle séduit les voyageurs en quête d'authenticité, de tranquillité et d'évasion.",
            "Dès votre arrivée, vous serez captivé par l'immensité de son sable doré, la beauté de ses eaux limpides et la richesse de la végétation tropicale qui l'entoure. Ici, l'océan Atlantique rencontre une nature préservée dans un décor spectaculaire, offrant un sentiment de liberté et de déconnexion absolue.",
            "Réputée pour ses vagues appréciées des amateurs de surf et de sensations douces, Monogaga est également un lieu idéal pour de longues promenades en bord de mer, l'observation de la faune et de la flore, ou simplement pour savourer le calme d'un environnement intact. Le murmure des vagues, la fraîcheur de l'air marin et l'horizon infini créent une ambiance propice à la détente et au ressourcement.",
            "À la tombée du jour, la plage dévoile l'un de ses plus beaux spectacles : des couchers de soleil flamboyants qui embrasent le ciel et l'océan de couleurs éclatantes, offrant des instants magiques et inoubliables.",
        ],
        closingParagraph:
            "Monogaga est bien plus qu'une plage : c'est une invitation à ralentir, à se reconnecter à la nature et à découvrir l'un des plus beaux joyaux du littoral ivoirien.",
        galleryImages: beachGalleryImages.monogaga,
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Liberté et déconnexion face à l'océan",
        activities: [
            {
                icon: "🏄",
                title: "Surf",
                description:
                    "Les vagues de Monogaga sont appréciées des amateurs de surf et de sensations douces dans un cadre naturel exceptionnel.",
            },
            {
                icon: "🚶",
                title: "Promenades en bord de mer",
                description:
                    "De longues balades sur le sable doré, entre océan Atlantique et végétation tropicale préservée.",
            },
            {
                icon: "🦜",
                title: "Faune & flore",
                description:
                    "Observez la richesse naturelle qui entoure la plage dans un environnement intact et apaisant.",
            },
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre océan et végétation tropicale.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "Pas d'électricité et de réseau internet sur place.",
            "Idéal pour une escapade.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Monogaga, entre San Pedro et Sassandra, District du Bas-Sassandra, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 370 km" },
                { label: "Temps de trajet", value: "5 h 30 à 6 h 30 par la route, selon les conditions de circulation" },
                { label: "Aéroport le plus proche", value: "Aéroport de San-Pédro" },
                {
                    label: "Centre de santé le plus proche",
                    value: "CHR de San-Pédro, principal établissement hospitalier de la région",
                },
                { label: "Température moyenne", value: "25 à 28 °C toute l'année" },
                {
                    label: "Hébergement",
                    value: "Maisons d'hôtes et hôtels à proximité de San-Pédro et Sassandra.",
                },
            ],
            activitiesSummary: "Surf, Promenades en bord de mer, Observation de la faune et de la flore, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "sassandra",
        name: "Sassandra",
        heroImage: "/images/ivoirexperience/the-place-to-be/sassandra.jpg",
        subtitle: "Port historique et plages atlantiques au cœur du Bas-Sassandra",
        heroTags: [
            { icon: "⚓", label: "Port de pêche" },
            { icon: "🏖️", label: "Plages" },
            { icon: "🌅", label: "Couchers de soleil" },
        ],
        welcomeTitle: "Sassandra – Entre estuaire, océan et traditions côtières",
        welcomeParagraphs: [
            "Située sur la côte ouest ivoirienne, Sassandra est une ville portuaire chargée d'histoire, nichée entre l'estuaire de la Sassandra et les vastes étendues de l'océan Atlantique. Véritable carrefour du littoral du Bas-Sassandra, elle séduit par son authenticité, son ambiance maritime et la beauté de ses paysages côtiers.",
            "Longtemps port d'exportation du café, du bois et du cacao, Sassandra conserve le charme d'une ville côtière vivante, où l'activité des pêcheurs, les pirogues colorées et les quais animés composent un tableau unique. La rencontre entre le fleuve et la mer offre des panoramas remarquables, propices à la contemplation et à la photographie.",
            "Les plages de Sassandra et de ses environs invitent à la détente, aux promenades en bord de mer et aux baignades dans un cadre tropical préservé. Entre cocoteraies, sable doré et horizon infini, le littoral sassandrais dévoile une Côte d'Ivoire authentique, loin des foules.",
            "À la tombée du jour, le port et la côte offrent des couchers de soleil spectaculaires sur l'Atlantique, moments privilégiés pour savourer la douceur de vivre de cette destination balnéaire encore méconnue.",
        ],
        closingParagraph:
            "Sassandra est une escale incontournable pour découvrir le littoral ouest ivoirien : un mélange harmonieux de patrimoine maritime, de nature préservée et d'hospitalité légendaire.",
        galleryImages: beachGalleryImages.sassandra,
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Découvrez Sassandra au rythme de l'océan",
        activities: [
            {
                icon: "🎣",
                title: "Pêche & port",
                description:
                    "Découvrez l'activité halieutique du port de Sassandra et l'ambiance authentique de ses quais et de ses pirogues.",
            },
            {
                icon: "🚶",
                title: "Promenades en bord de mer",
                description:
                    "Longues balades sur les plages et le long de l'estuaire, entre sable fin, cocotiers et brise marine.",
            },
            {
                icon: "🚣",
                title: "Excursions en pirogue",
                description:
                    "Explorez l'estuaire de la Sassandra et les paysages lagunaires en pirogue avec les habitants du littoral.",
            },
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre estuaire, plages et cocoteraies.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "Particulièrement les villages de Drewin et Niéga : pas d'électricité et de réseau internet sur place.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Sassandra, District du Bas-Sassandra, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 360 km" },
                { label: "Temps de trajet", value: "5 h à 6 h par la route, selon les conditions de circulation" },
                { label: "Aéroport le plus proche", value: "Aéroport de San-Pédro" },
                {
                    label: "Centre de santé le plus proche",
                    value: "Hôpital Général de Sassandra",
                },
                { label: "Température moyenne", value: "25 à 28 °C toute l'année" },
                {
                    label: "Hébergement",
                    value: "Hôtels, maisons d'hôtes et résidences à Sassandra et dans les environs.",
                },
            ],
            activitiesSummary: "Pêche, Promenades en bord de mer, Excursions en pirogue, Baignade, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "assinie",
        name: "Assinie",
        heroImage: beachGalleryImages.assinie[0],
        subtitle: "Le joyau balnéaire de la Côte d'Ivoire",
        heroTags: [
            { icon: "🏖️", label: "Plages de sable blanc" },
            { icon: "🌊", label: "Océan & lagune" },
            { icon: "☀️", label: "Cadre tropical" },
        ],
        welcomeTitle: "Assinie – Le joyau balnéaire de la Côte d'Ivoire",
        welcomeParagraphs: [
            "Envie d'évasion, de détente et de paysages de carte postale ? Assinie est une destination incontournable qui séduit par ses plages de sable blanc, ses eaux cristallines et son atmosphère tropicale unique. Entre océan Atlantique et lagune, ce petit coin de paradis offre un cadre idéal pour se ressourcer, pratiquer des activités nautiques ou simplement profiter de moments inoubliables au bord de l'eau.",
        ],
        closingParagraph:
            "Assinie vous promet une expérience unique mêlant détente, nature et dépaysement. Que vous soyez en quête d'aventure, de romantisme ou simplement de repos, ses plages paradisiaques sauront vous séduire et vous offrir des souvenirs mémorables.",
        galleryImages: beachGalleryImages.assinie,
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Entre océan, lagune et sensations fortes",
        activities: [
            {
                icon: "🏖️",
                title: "Baignade & farniente",
                description:
                    "Profitez des plages de sable blanc et des eaux cristallines d'Assinie pour la baignade et la détente au bord de l'océan.",
            },
            {
                icon: "🏄",
                title: "Kitesurf & paddle",
                description:
                    "Les conditions de vent et les eaux calmes de la lagune font d'Assinie une destination prisée pour le kitesurf et le paddle.",
            },
            {
                icon: "🏍️",
                title: "Quad biking",
                description:
                    "Partez à l'aventure en quad entre plages, pistes côtières et paysages lagunaires pour une découverte dynamique d'Assinie.",
            },
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre plages de sable blanc et cocotiers.",
            },
        ],
        highlightBeaches: [
            {
                title: "Assinie Beach",
                description:
                    "La plage emblématique d'Assinie. Son sable fin, ses eaux turquoise et son ambiance chaleureuse en font l'endroit parfait pour la baignade, le farniente et les activités nautiques.",
            },
            {
                title: "Assouindé",
                description:
                    "Plus sauvage et préservée, cette magnifique plage séduit les amoureux de la nature et de la tranquillité. Un lieu idéal pour de longues promenades, des séances photo spectaculaires et des instants de pure sérénité.",
            },
            {
                title: "La Passe",
                description:
                    "Site naturel exceptionnel où la lagune Aby rencontre l'océan Atlantique. Ce panorama unique offre des couchers de soleil à couper le souffle et constitue l'un des plus beaux spots photographiques de la région.",
            },
            {
                title: "Plage de La Désirade",
                description:
                    "Un véritable havre de paix bordé de cocotiers et de palmiers. Parfaite pour se détendre à l'ombre, savourer la douceur du climat tropical et profiter d'un cadre élégant et reposant.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Assinie, Région du Sud-Comoé, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 80 km" },
                { label: "Temps de trajet", value: "1 h 30 à 2 h selon la circulation" },
                { label: "Aéroport le plus proche", value: "Aéroport International Félix Houphouët-Boigny d'Abidjan (ABJ)" },
                {
                    label: "Centre de santé le plus proche",
                    value: "Centre de Santé Urbain d'Assinie (Hôpital Général d'Adiaké à proximité)",
                },
                { label: "Température moyenne", value: "26 à 28 °C toute l'année" },
                {
                    label: "Hébergement",
                    value: "Hôtels, ressorts, villas privées, maisons d'hôtes et résidences",
                },
            ],
            activitiesSummary: "Baignade, Kitesurf, Paddle, Pirogue sur la lagune, Quad biking, Farniente, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "grand-bereby",
        name: "Grand-Béréby",
        heroImage: beachGalleryImages["grand-bereby"][0],
        subtitle: "L'élégance naturelle du littoral ivoirien, entre océan, lagunes et forêts tropicales",
        heroTags: [
            { icon: "🌿", label: "Nature préservée" },
            { icon: "🐢", label: "Faune marine" },
            { icon: "🌅", label: "Couchers de soleil" },
        ],
        welcomeTitle: "Grand-Béréby – L'élégance naturelle du littoral ivoirien",
        welcomeParagraphs: [
            "Située à l'extrême sud-ouest de la Côte d'Ivoire, entre océan, lagunes et forêts tropicales, Grand-Béréby est une destination balnéaire de charme qui séduit par la beauté de ses paysages et l'authenticité de son environnement. Véritable havre de paix, cette station côtière est une invitation à la détente, à l'aventure et à la découverte des merveilles naturelles du littoral ivoirien.",
            "Dès les premiers instants, Grand-Béréby enchante ses visiteurs par ses longues plages de sable fin bordées de cocotiers, ses eaux scintillantes et son atmosphère paisible. Ici, l'océan Atlantique offre un spectacle permanent, entre vagues majestueuses, brise marine rafraîchissante et panoramas d'une rare beauté.",
            "Destination privilégiée des amoureux de la nature et du tourisme balnéaire, Grand-Béréby propose une multitude d'expériences. Les visiteurs peuvent profiter de promenades en bord de mer, d'excursions en bateau, de sorties de pêche, d'observation de la faune marine ou encore de découvertes des villages côtiers qui témoignent de la richesse culturelle de la région.",
            "La ville est également réputée pour son accueil chaleureux, sa gastronomie à base de produits frais de la mer et ses paysages préservés où se rencontrent océan, lagunes et végétation luxuriante. Chaque séjour est une occasion de vivre une expérience authentique au rythme de la nature et des traditions locales.",
            "À la tombée du jour, Grand-Béréby dévoile l'un de ses plus beaux spectacles. Le soleil se fond lentement dans l'horizon, illuminant le ciel et l'océan de teintes dorées, orangées et pourpres, créant une ambiance magique qui marque les esprits.",
        ],
        closingParagraph:
            "Grand-Béréby est bien plus qu'une destination balnéaire : c'est une parenthèse d'évasion où la beauté du littoral, la richesse culturelle et la douceur de vivre se conjuguent pour offrir l'une des plus belles expériences touristiques de la Côte d'Ivoire.",
        galleryImages: beachGalleryImages["grand-bereby"],
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Découvrez Grand-Béréby au rythme de l'océan",
        activities: [
            {
                icon: "🚶",
                title: "Promenades en bord de mer",
                description:
                    "Parcourez les longues plages de sable fin bordées de cocotiers dans un cadre paisible et préservé.",
            },
            {
                icon: "⛵",
                title: "Excursions en bateau",
                description:
                    "Explorez le littoral et les lagunes environnantes lors d'excursions en mer ou en pirogue.",
            },
            {
                icon: "🐢",
                title: "Faune marine",
                description:
                    "Observez la richesse de la faune marine et découvrez les villages côtiers de la région.",
            },
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre plages, lagunes et forêts tropicales.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Grand-Béréby, District du Bas-Sassandra, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 400 km" },
                { label: "Temps de trajet", value: "5 h à 6 h par la route, selon les conditions de circulation" },
                { label: "Aéroport le plus proche", value: "Aéroport de San-Pédro" },
                {
                    label: "Centre de santé le plus proche",
                    value: "CHR de San-Pédro, principal établissement hospitalier de la région",
                },
                { label: "Température moyenne", value: "25 à 28 °C toute l'année" },
                {
                    label: "Hébergement",
                    value: "Maisons d'hôtes et hôtels à proximité de San-Pédro et Grand-Béréby.",
                },
            ],
            activitiesSummary: "Promenades en bord de mer, Excursions en bateau, Pêche, Observation de la faune marine, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "mani-bereby",
        name: "Mani-Béréby",
        heroImage: "/images/ivoirexperience/the-place-to-be/mani-bereby.jpg",
        subtitle: "Entre lagune, océan et nature préservée, à proximité de San Pedro",
        heroTags: [
            { icon: "⛵", label: "Pirogue" },
            { icon: "🌿", label: "Nature préservée" },
            { icon: "🏝️", label: "Îles Ehotilé" },
        ],
        welcomeTitle: "Mani-Béréby – Entre lagune, océan et nature préservée",
        welcomeParagraphs: [
            "Située à l'extrême sud-ouest de la Côte d'Ivoire, à proximité de San Pedro, Mani-Béréby est une destination authentique où la beauté des paysages côtiers se mêle à la richesse d'une nature encore préservée. Ce village balnéaire séduit les visiteurs en quête de calme, de découverte et d'expériences uniques au contact de l'océan Atlantique.",
            "Dès les premiers instants, Mani-Béréby charme par ses vastes plages de sable fin bordées de cocotiers, ses eaux scintillantes et son atmosphère paisible. Entre mer, lagunes et forêts tropicales, le cadre offre un véritable havre de sérénité, loin de l'agitation urbaine.",
            "Destination privilégiée des amoureux de la nature, Mani-Béréby est également la porte d'entrée vers des sites exceptionnels, notamment les îles Ehotilé et les espaces naturels du littoral sud-ouest. Les visiteurs peuvent profiter de balades en pirogue, d'excursions en mer, de découvertes culturelles ou encore de moments de détente face à l'immensité de l'océan.",
            "La région est également réputée pour sa richesse halieutique et son hospitalité légendaire. Les saveurs locales, les produits frais de la pêche et les traditions des communautés côtières offrent une immersion authentique dans l'art de vivre du sud-ouest ivoirien.",
            "Lorsque le soleil se couche sur l'horizon, Mani-Béréby révèle toute sa magie. Les reflets dorés sur l'océan, le bruit apaisant des vagues et la douceur du climat créent une atmosphère inoubliable, propice à la contemplation et à l'évasion.",
        ],
        closingParagraph:
            "Mani-Béréby est bien plus qu'une destination balnéaire : c'est une expérience unique où nature, culture et authenticité se rencontrent pour offrir aux visiteurs l'un des plus beaux visages de la Côte d'Ivoire.",
        galleryImages: beachGalleryImages["mani-bereby"],
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Nature, culture et authenticité au sud-ouest",
        activities: [
            {
                icon: "⛵",
                title: "Balades en pirogue",
                description:
                    "Explorez les lagunes et les espaces naturels du littoral sud-ouest en pirogue traditionnelle.",
            },
            {
                icon: "🏝️",
                title: "Îles Ehotilé",
                description:
                    "Partez à la découverte des îles Ehotilé, joyaux naturels accessibles depuis Mani-Béréby.",
            },
            {
                icon: "🎣",
                title: "Pêche & gastronomie",
                description:
                    "Savourez les produits frais de la pêche et découvrez les traditions des communautés côtières.",
            },
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, face à l'océan et aux lagunes environnantes.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "Problème de réseau internet et d'eau courante.",
            "Idéal pour une escapade.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Mani-Béréby, District du Bas-Sassandra, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 390 km" },
                { label: "Temps de trajet", value: "5 h à 6 h par la route, selon les conditions de circulation" },
                { label: "Aéroport le plus proche", value: "Aéroport de San-Pédro" },
                {
                    label: "Centre de santé le plus proche",
                    value: "CHR de San-Pédro, principal établissement hospitalier de la région",
                },
                { label: "Température moyenne", value: "25 à 28 °C toute l'année" },
                {
                    label: "Hébergement",
                    value: "Maisons d'hôtes et hôtels à proximité de San-Pédro.",
                },
            ],
            activitiesSummary: "Balades en pirogue, Excursions en mer, Découvertes culturelles, Observation de la nature, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "grand-lahou",
        name: "Grand-Lahou",
        heroImage: beachGalleryImages["grand-lahou"][0],
        subtitle: "Là où la lagune rencontre l'océan, à l'embouchure du fleuve Bandama",
        heroTags: [
            { icon: "🌊", label: "Lagune & océan" },
            { icon: "🚣", label: "Pirogue" },
            { icon: "🌿", label: "Nature sauvage" },
        ],
        welcomeTitle: "Plage de Grand-Lahou – Là où la lagune rencontre l'océan",
        welcomeParagraphs: [
            "Nichée sur la côte sud de la Côte d'Ivoire, à l'embouchure du fleuve Bandama, la plage de Grand-Lahou est une destination unique où l'océan Atlantique, les lagunes et les cours d'eau se rencontrent dans un décor naturel d'une rare beauté. Véritable havre de paix, elle séduit les voyageurs en quête d'authenticité, de calme et de dépaysement.",
            "Dès votre arrivée, vous serez émerveillé par l'immensité de son littoral, la douceur de son sable doré et l'harmonie parfaite entre les eaux de la lagune et celles de l'océan. Ici, la nature offre un spectacle permanent, façonné par les marées, les vents marins et les paysages sauvages qui entourent la côte.",
            "Réputée pour son cadre exceptionnel et son atmosphère paisible, la plage de Grand-Lahou est idéale pour les longues promenades en bord de mer, les excursions en pirogue sur la lagune ou simplement pour profiter d'un moment de détente face à l'horizon. Le chant des oiseaux, le bruissement des cocotiers et le murmure des vagues créent une ambiance propice au ressourcement et à la contemplation.",
            "Au fil de la journée, les visiteurs découvrent un environnement riche où la faune, la flore et les activités traditionnelles de pêche participent au charme de la destination. La rencontre entre les paysages lagunaires et l'immensité de l'Atlantique fait de Grand-Lahou un lieu unique sur le littoral ivoirien.",
            "À la tombée du jour, la plage se transforme en un véritable tableau naturel. Les reflets du soleil couchant illuminent les eaux de nuances dorées et orangées, tandis que l'horizon se pare de couleurs éclatantes, offrant un spectacle aussi apaisant qu'inoubliable.",
        ],
        closingParagraph:
            "Grand-Lahou est bien plus qu'une plage : c'est une invitation à découvrir la rencontre fascinante entre lagune et océan, à se reconnecter à la nature et à explorer l'un des joyaux les plus singuliers du littoral ivoirien.",
        galleryImages: beachGalleryImages["grand-lahou"],
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Entre lagune, océan et nature préservée",
        activities: [
            {
                icon: "🚶",
                title: "Promenades en bord de mer",
                description:
                    "Longues balades sur le sable doré, entre lagune et océan Atlantique.",
            },
            {
                icon: "🚣",
                title: "Excursions en pirogue",
                description:
                    "Découvrez les paysages lagunaires et les villages de pêcheurs lors d'excursions en pirogue.",
            },
            {
                icon: "🦜",
                title: "Faune & flore",
                description:
                    "Observez la richesse naturelle de cette destination unique à l'embouchure du Bandama.",
            },
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre lagune, océan et forêt tropicale.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Grand-Lahou, District des Lagunes, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 150 km" },
                { label: "Temps de trajet", value: "2 h à 2 h 30 par la route, selon les conditions de circulation" },
                { label: "Aéroport le plus proche", value: "Aéroport International Félix Houphouët-Boigny d'Abidjan (ABJ)" },
                {
                    label: "Centre de santé le plus proche",
                    value: "Centre de santé de Grand-Lahou (hôpitaux d'Abidjan à proximité)",
                },
                { label: "Température moyenne", value: "26 à 28 °C toute l'année" },
                {
                    label: "Hébergement",
                    value: "Maisons d'hôtes et hébergements locaux à Grand-Lahou et environs.",
                },
            ],
            activitiesSummary: "Promenades en bord de mer, Excursions en pirogue, Observation de la faune et de la flore, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "dagbego",
        name: "Dagbego",
        heroImage: beachGalleryImages.dagbego[0],
        subtitle: "Un joyau du littoral lagunaire ivoirien",
        heroTags: [
            { icon: "🌿", label: "Lagune" },
            { icon: "🏖️", label: "Plage" },
            { icon: "🌊", label: "Eaux calmes" },
        ],
        welcomeTitle: "Dagbego – Entre lagune et forêt tropicale",
        welcomeParagraphs: [
            "Dagbego est l'une des perles du littoral lagunaire ivoirien, un lieu où les eaux calmes, la végétation luxuriante et l'authenticité des villages côtiers se conjuguent pour offrir un cadre d'exception.",
            "Ce site préservé séduit par la rencontre harmonieuse entre lagune, forêt tropicale et espaces côtiers, invitant les visiteurs à la détente, à la contemplation et à la découverte d'une Côte d'Ivoire encore méconnue.",
        ],
        galleryImages: beachGalleryImages.dagbego,
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Entre lagune, forêt tropicale et littoral préservé",
        activities: [
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre lagune calme et végétation tropicale.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "Problème de réseau internet et d'eau courante.",
            "Idéal pour une escapade.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Dagbego, District des Lagunes, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 160 km" },
                { label: "Aéroport le plus proche", value: "Aéroport International Félix Houphouët-Boigny d'Abidjan (ABJ)" },
            ],
            activitiesSummary: "Excursions en pirogue, Promenades, Observation de la nature, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "baie-des-milliardaires",
        name: "Baie des Milliardaires",
        heroImage: beachGalleryImages["baie-des-milliardaires"][0],
        subtitle: "Un littoral d'exception aux portes d'Assinie",
        heroTags: [
            { icon: "🏖️", label: "Plage chic" },
            { icon: "🌴", label: "Cocotiers" },
            { icon: "☀️", label: "Eaux turquoise" },
        ],
        welcomeTitle: "Baie des Milliardaires – L'élégance du littoral assiniekois",
        welcomeParagraphs: [
            "La Baie des Milliardaires est l'un des plus beaux trésors du littoral assiniekois. Entre cocotiers, sable fin et eaux turquoise, ce site offre un cadre préservé et raffiné, idéal pour la détente et l'évasion.",
            "Proche d'Assinie, cette baie séduit par son atmosphère exclusive et ses paysages de carte postale, parfaits pour savourer la douceur de vivre de la Côte d'Ivoire.",
        ],
        galleryImages: beachGalleryImages["baie-des-milliardaires"],
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Détente et évasion au cœur du littoral assiniekois",
        activities: [
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre cocotiers, sable fin et eaux turquoise.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Baie des Milliardaires, Assinie, Région du Sud-Comoé, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 85 km" },
                { label: "Temps de trajet", value: "1 h 30 à 2 h selon la circulation" },
                { label: "Aéroport le plus proche", value: "Aéroport International Félix Houphouët-Boigny d'Abidjan (ABJ)" },
            ],
            activitiesSummary: "Baignade, Farniente, Promenades en bord de mer, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "fresco",
        name: "Fresco",
        heroImage: beachGalleryImages.fresco[0],
        subtitle: "Une perle de la côte ouest ivoirienne",
        heroTags: [
            { icon: "🎣", label: "Pêche" },
            { icon: "🌅", label: "Couchers de soleil" },
            { icon: "🏖️", label: "Plages sauvages" },
        ],
        welcomeTitle: "Fresco – Authenticité et beauté sauvage du littoral ouest",
        welcomeParagraphs: [
            "Fresco est une destination emblématique de la côte ouest ivoirienne, réputée pour ses plages sauvages, son ambiance de village de pêcheurs et ses paysages côtiers d'une beauté remarquable.",
            "Entre océan Atlantique, végétation tropicale et traditions halieutiques, Fresco offre une expérience authentique au cœur de l'un des plus beaux littoraux du pays.",
        ],
        galleryImages: beachGalleryImages.fresco,
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Authenticité et nature sur la côte ouest",
        activities: [
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre plages sauvages et village de pêcheurs.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "Problème de réseau internet et d'eau courante.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Fresco, District du Bas-Sassandra, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 350 km" },
                { label: "Aéroport le plus proche", value: "Aéroport de San-Pédro" },
            ],
            activitiesSummary: "Pêche, Promenades en bord de mer, Observation de la nature, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "kosso",
        name: "Kosso",
        heroImage: beachGalleryImages.kosso[0],
        subtitle: "Un littoral préservé du sud-ouest ivoirien",
        heroTags: [
            { icon: "🌿", label: "Sauvage" },
            { icon: "🏖️", label: "Plage" },
            { icon: "🌊", label: "Lagune" },
        ],
        welcomeTitle: "Kosso – Nature préservée entre lagune et forêt",
        welcomeParagraphs: [
            "Kosso est un joyau méconnu du sud-ouest ivoirien, où lagune, forêt tropicale et plages encore préservées offrent un cadre exceptionnel pour les amoureux de nature et d'authenticité.",
            "Ce site invite à la détente et à la découverte d'un littoral sauvage, loin de l'agitation, dans un environnement où la nature règne en maître.",
        ],
        galleryImages: beachGalleryImages.kosso,
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Nature préservée entre lagune et forêt",
        activities: [
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre lagune, forêt tropicale et plages préservées.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "Problème de réseau internet et d'eau courante.",
            "Idéal pour une escapade.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Kosso, District du Bas-Sassandra, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 400 km" },
                { label: "Aéroport le plus proche", value: "Aéroport de San-Pédro" },
            ],
            activitiesSummary: "Promenades, Observation de la nature, Excursions en pirogue, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "tabou",
        name: "Tabou",
        heroImage: beachGalleryImages.tabou[0],
        subtitle: "Porte d'entrée du littoral extrême sud-ouest",
        heroTags: [
            { icon: "🗺️", label: "Sud-ouest" },
            { icon: "🏖️", label: "Littoral" },
            { icon: "🌿", label: "Authenticité" },
        ],
        welcomeTitle: "Tabou – Au cœur du littoral ivoirien",
        welcomeParagraphs: [
            "Tabou est une commune dynamique du sud-ouest ivoirien et un point de départ incontournable vers les plus belles plages de la région, dont la célèbre Tolou Plage.",
            "Entre villages côtiers, paysages préservés et traditions locales, Tabou offre une immersion authentique dans l'art de vivre du littoral extrême sud-ouest de la Côte d'Ivoire.",
        ],
        galleryImages: beachGalleryImages.tabou,
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Découvrez le littoral extrême sud-ouest",
        activities: [
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, à proximité des plages et villages côtiers de la région.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "Idéal pour une escapade.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Tabou, District du Bas-Sassandra, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 430 km" },
                { label: "Aéroport le plus proche", value: "Aéroport de San-Pédro" },
            ],
            activitiesSummary: "Découverte des plages environnantes, Excursions culturelles, Promenades, Camping",
            emergencyNumbers,
        },
    },
    {
        slug: "taki",
        name: "Taki",
        heroImage: beachGalleryImages.taki[0],
        subtitle: "Un havre de paix sur le littoral ivoirien",
        heroTags: [
            { icon: "🌊", label: "Lagune" },
            { icon: "🌴", label: "Tropical" },
            { icon: "☀️", label: "Sérénité" },
        ],
        welcomeTitle: "Taki – Sérénité face à l'horizon",
        welcomeParagraphs: [
            "Taki est un havre de paix sur le littoral ivoirien, entre lagune paisible, cocotiers et eaux calmes propices à la détente et à la contemplation.",
            "Ce site préservé invite à ralentir le rythme et à savourer la beauté naturelle du sud-ouest ivoirien dans un cadre apaisant et authentique.",
        ],
        galleryImages: beachGalleryImages.taki,
        activitiesTitle: "Activités et loisirs",
        activitiesSubtitle: "Sérénité et nature au bord de la lagune",
        activities: [
            {
                icon: "⛺",
                title: "Camping",
                description:
                    "Passez la nuit au plus près de la nature en camping ou en bivouac, entre lagune paisible et cocotiers.",
            },
        ],
        safetyGuidelines: [
            "Utilisez des transports agréés.",
            "Respectez les consignes de baignade et de sécurité.",
            "Hydratez-vous et protégez-vous du soleil.",
            "Préservez l'environnement et respectez les communautés locales.",
            "Problème de réseau internet et d'eau courante.",
            "Idéal pour une escapade.",
            "En cas d'urgence, contactez les autorités compétentes.",
        ],
        practicalInformation: {
            location: "Taki, District du Bas-Sassandra, Côte d'Ivoire",
            bestPeriod: "D'octobre à mai pour profiter d'un ensoleillement optimal.",
            usefulInfo: [
                { label: "Distance depuis Abidjan", value: "environ 400 km" },
                { label: "Aéroport le plus proche", value: "Aéroport de San-Pédro" },
            ],
            activitiesSummary: "Détente, Promenades, Excursions en pirogue, Camping",
            emergencyNumbers,
        },
    },
];

export function getBeachDestinationDetail(slug: string) {
    return beachDestinationDetails.find((destination) => destination.slug === slug);
}

export function getRelatedBeachDestinations(slug: string) {
    return beachDestinationDetails.filter((destination) => destination.slug !== slug).slice(0, 3);
}
